import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FilterGroup, FilterOption, reduceFilterGroupToSelectedOptions } from '@app/components/filter/filter.component';
import { AnalyticEvents } from '@app/enums';
import { QueryFetcher } from '@app/models/core/query-fetcher.model';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { Question } from '@app/models/recruiting/question.model';
import { AnalyticService, NotifyService } from '@app/services';
import { AccessLevelDisplayOptions } from '@app/types/translatables/access-level-display.options';
import { jobPostingType } from '../../meta/question-types';

@Component({
    selector: 'app-previous-question-dialog',
    templateUrl: './previous-question-dialog.template.html',
    styleUrls: ['./previous-question-dialog.style.scss'],
})
export class PreviousQuestionDialog {
    @Input() openDialog = false;
    @Input() header = 'applicant-tracker.previous-question-dialog.addAQuestion';
    @Input() isAutoArchive = false;
    @Input() jobPosting: JobPosting;

    @Output() onClose = new EventEmitter();
    @Output() questionsAdded = new EventEmitter<Question[]>();

    baseQuery: QueryFetcher;
    searchWidth = 250;
    searchPlaceholder = 'search';
    jobPostingType = jobPostingType;

    accessLevelDisplay = AccessLevelDisplayOptions;

    questionTypeFilter = {
        label: 'applicant-tracker.components.previous-question-dialog.questionType',
        options: [
            { label: 'questionType.singleLineCapitalized', selected: false, value: 4 },
            { label: 'questionType.multiLineCapitalized', selected: false, value: 3 },
            { label: 'questionType.number', selected: false, value: 2 },
            { label: 'questionType.date', selected: false, value: 1 },
        ],
    };

    filterGroups: Array<FilterGroup> = [this.questionTypeFilter];

    questions: Question[];

    query: string;

    constructor(
        private notify: NotifyService,
        private analyticService: AnalyticService
    ) {}

    async ngOnInit(): Promise<void> {
        this.baseQuery = Question.limit(30)
            .where('isAutoArchive', this.isAutoArchive)
            .when(this.jobPosting?.id, (query) => query.where('excludeJobPostingId', this.jobPosting.id));

        try {
            const [questions] = await this.baseQuery.all();

            // Filter out those questions that has same question text.
            this.questions = questions.filter(
                (question, i, questionsArray) => questionsArray.findIndex((t) => t.question === question.question) === i
            );

            this.clearIdsAndJobId();
        } catch (err) {
            this.notify.error(err);
        }
    }

    closeDialog(): void {
        this.onClose.emit();
    }

    onAdd(): void {
        // Before adding clear so that these are considered new questions.
        this.clearIdsAndJobId();

        const selectedQuestions = this.questions.filter((question: Question) => {
            return question.isSelected;
        });

        this.questionsAdded.emit(selectedQuestions);

        this.analyticService.trackEvent(this.getEvent());

        this.closeDialog();
    }

    async onSearch(query: string): Promise<void> {
        this.query = query;

        try {
            const [questions] = await this.baseQuery.where('query', query).all();

            // Filter out those questions that has same question text.
            this.questions = questions.filter(
                (question, i, questionsArray) => questionsArray.findIndex((t) => t.question === question.question) === i
            );
        } catch (err) {
            this.notify.error(err);
        }
    }

    async onFilter(): Promise<void> {
        const selectedQuestionType = reduceFilterGroupToSelectedOptions(this.questionTypeFilter).map(
            (option: FilterOption) => option.value
        );

        if (!selectedQuestionType.length) {
            try {
                const [questions] = await this.baseQuery.where('query', this.query).all();

                this.questions = questions;
            } catch (err) {
                this.notify.error(err);
            }

            return;
        }

        try {
            const [questions] = await this.baseQuery
                .where('query', this.query)
                .whereIn('atsJobPostingQuestionTypeId', selectedQuestionType)
                .all();

            this.questions = questions;
        } catch (err) {
            this.notify.error(err);
        }
    }

    private clearIdsAndJobId(): void {
        this.questions.forEach((question: Question) => {
            // Clear out the jobId and id since this will be used as a new question with a new jobId
            question.id = null;
            question.jobId = null;
        });
    }

    private getEvent(): AnalyticEvents {
        if (this.isAutoArchive) {
            return this.jobPosting?.isPersisted
                ? AnalyticEvents.AddAutoArchiveQuestionFromPreviousOnExistingPosting
                : AnalyticEvents.AddAutoArchiveQuestionFromPreviousOnNewJobPosting;
        }

        return this.jobPosting?.isPersisted
            ? AnalyticEvents.AddAdditionalQuestionFromPreviousOnExistingPosting
            : AnalyticEvents.AddAdditionalQuestionFromPreviousOnNewJobPosting;
    }
}
